import { ref, uploadBytes } from 'firebase/storage';
import { storage } from '../config/firebase';
import type { ChatAttachment } from '@researcher/shared';
import {
  ALLOWED_ATTACHMENT_MIME_TYPES,
  MAX_ATTACHMENT_SIZE_BYTES,
  MAX_ATTACHMENTS_PER_MESSAGE,
} from '@researcher/shared';

export interface PendingAttachment {
  id: string;
  file: File;
  /** Object URL for thumbnail preview before upload. */
  previewUrl: string;
  status: 'pending' | 'uploading' | 'ready' | 'error';
  error?: string;
  uploaded?: ChatAttachment;
}

/**
 * Quick client-side validation before the user even tries to upload. The
 * Storage bucket rules enforce these again on write — this is just so we can
 * reject early with a friendly message.
 */
export function validateFile(file: File): { valid: true } | { valid: false; error: string } {
  if (!(ALLOWED_ATTACHMENT_MIME_TYPES as readonly string[]).includes(file.type)) {
    return {
      valid: false,
      error: `Unsupported file type. Please upload an image (JPEG, PNG, HEIC, WebP) or PDF.`,
    };
  }
  if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
    const mb = (MAX_ATTACHMENT_SIZE_BYTES / 1024 / 1024).toFixed(0);
    return { valid: false, error: `File is too large. Maximum size is ${mb} MB.` };
  }
  return { valid: true };
}

export { MAX_ATTACHMENTS_PER_MESSAGE };

/**
 * Sanitize a filename for use in a Storage path. Storage doesn't love
 * slashes / control chars / unicode that some camera apps emit.
 */
function sanitizeFilename(name: string): string {
  const base = name
    .replace(/[/\\]/g, '_')
    .replace(/[\x00-\x1f]/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 80);
  return base || 'attachment';
}

/**
 * Upload a single file to Firebase Storage under the user's chat-uploads path.
 * Path shape: `chat-uploads/{userId}/{messageId}/{filename}` — keyed by the
 * pending-message id so multiple attachments for the same message live together
 * and the API can validate ownership by checking the userId prefix.
 */
export async function uploadAttachment(
  file: File,
  userId: string,
  messageId: string,
): Promise<ChatAttachment> {
  const filename = sanitizeFilename(file.name);
  const storagePath = `chat-uploads/${userId}/${messageId}/${Date.now()}_${filename}`;
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file, {
    contentType: file.type,
    cacheControl: 'private, max-age=0, no-store',
  });

  return {
    storagePath,
    mimeType: file.type,
    filename,
    sizeBytes: file.size,
  };
}
